import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";

export const useOrderDetails = ({ orderId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const { orderId: OrderIdFromParams } = useParams();
  const isMounted = useRef(true);

  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGoBack = () => {
    navigate(-1);
  };

  // paramId now holds the ONDC Order ID (e.g., "ORDER2010") if you applied the fix above.
  const paramId = orderId || OrderIdFromParams;

  // Use useCallback to memoize the fetch function
  const fetchOrderDetails = useCallback(async () => {
    if (!paramId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await apiHandler({
        // URL is now correctly constructed: /orders/getOrderDetails/ORDER2010 
        url: `${endpoint.GET_ORDER_DETAIL}?id=${paramId}`,
        method: "GET",
        authToken: authData,
        dispatch,
        storeId,
      });

      //console.log("order details", result);
      

      if (isMounted.current && result?.data?.data) {
        setOrderData(result.data.data);
      }
    } catch (err) {
      //console.error("Error fetching order details:", err);
      if (isMounted.current) setError("Failed to fetch order details.");
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [paramId, authData, dispatch, storeId]); // Dependencies required for useCallback

  useEffect(() => {
    fetchOrderDetails();

    return () => {
      isMounted.current = false;
    };
  }, [fetchOrderDetails]); // fetchOrderDetails is the dependency

  // ... (Other functions like calculateDaysRemaining, isReturnExpired, etc., remain the same)
  const calculateDaysRemaining = () => {
    if (!orderData?.order_details?.return_expires_at) return 0;
    const today = new Date();
    const expiryDate = new Date(orderData.order_details.return_expires_at);
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const isReturnExpired = () => calculateDaysRemaining() === 0;

  const handleGetInvoice = () => {
    const invoiceUrl = orderData?.order_details?.invoice_url;
    if (invoiceUrl) {
      window.open(invoiceUrl, "_blank");
    }
  };

  const getProviderDetails = () => orderData?.provider_details;

  return {
    orderData,
    loading,
    error,
    calculateDaysRemaining,
    isReturnExpired,
    handleGetInvoice,
    handleGoBack,
    getProviderDetails,
  };
};